// Global
import { Placeholder, RouteData, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import AlgoliaSearch from 'components/helpers/AlgoliaSearch/AlgoliaSearch';
import { AlgoliaSearchProps } from 'components/helpers/AlgoliaSearch/SearchTypes';
import BackToTopButton from 'components/helpers/BackToTop/BackToTop';
import HeadMeta from 'components/helpers/Head/HeadMeta';
import HeadNRCFonts from 'components/helpers/Head/HeadNRCFonts/HeadNRCFonts';
import SCcdp from 'components/helpers/Head/SitecoreCDP';
import SitecoreKeepAlive from 'components/helpers/SitecoreKeepAlive';
import { IsNRC } from 'lib/util/site-configuration';
import { InstantSearchServerState, InstantSearchSSRProvider } from 'react-instantsearch-hooks-web';

interface SearchWrapperProps extends AlgoliaSearchProps {
  pageEditing: boolean;
  isSearchRobot: boolean;
}

interface ServerSideSearchWrapperProps {
  children: React.ReactNode | React.ReactNode[];
  algoliaServerState?: InstantSearchServerState;
  isSearchRobot: boolean;
}

const ServerSideSearchWrapper = ({
  children,
  algoliaServerState,
}: ServerSideSearchWrapperProps): JSX.Element => {
  // If we are client-side, then skip this component.
  if (typeof window !== `undefined`) {
    return <>{children}</>;
  }
  // If we made it this far we are running server-side on a site that requires authentication...
  // Let InstaSearchSSR render for us
  return <InstantSearchSSRProvider {...algoliaServerState}>{children}</InstantSearchSSRProvider>;
};

const SearchWrapper = ({
  pageEditing,
  children,
  isSearchRobot,
  algoliaServerState,
  ifRouting,
  searchUrl,
}: SearchWrapperProps): JSX.Element => {
  if (pageEditing === true) {
    return <>{children}</>;
  }
  return (
    <div id="inPageLayout">
      <ServerSideSearchWrapper
        isSearchRobot={isSearchRobot}
        algoliaServerState={algoliaServerState}
      >
        <div id="inSearchWrapper">
          <AlgoliaSearch ifRouting={ifRouting} searchUrl={searchUrl}>
            <div id="inAlgoliaSearch">{children}</div>
          </AlgoliaSearch>
        </div>
      </ServerSideSearchWrapper>
    </div>
  );
};

const PageLayout = ({
  algoliaServerState,
  isSearchRobot,
}: {
  algoliaServerState?: InstantSearchServerState;
  isSearchRobot: boolean;
}): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;

  const route = sitecoreContext.route as RouteData;
  const isSearchPage =
    sitecoreContext.itemPath === sitecoreContext.searchPageUrl ||
    sitecoreContext.hasSearch === true;

  const siteSpecificClass = sitecoreContext.siteSpecificClass?.trim();

  return (
    <>
      <HeadMeta />
      {IsNRC(sitecoreContext) && <HeadNRCFonts />}
      <SearchWrapper
        isSearchRobot={isSearchRobot}
        algoliaServerState={algoliaServerState}
        ifRouting={isSearchPage}
        searchUrl={sitecoreContext.searchPageUrl}
        pageEditing={pageEditing}
      >
        <Placeholder name="nmdp-gtm" rendering={route} />
        <Placeholder name="siteimprove" rendering={route} />
        <header className={siteSpecificClass}>
          <Placeholder name="nmdp-banner" rendering={route} />
          <SCcdp />
          <Placeholder name="nmdp-header" rendering={route} />
          <Placeholder name="nmdp-breadcrumb" rendering={route} />
        </header>
        <main className={siteSpecificClass}>
          <Placeholder name="nmdp-hero" rendering={route} />
          <Placeholder name="nmdp-main" rendering={route} />
          <BackToTopButton />
        </main>
        <footer className={siteSpecificClass}>
          <Placeholder name="nmdp-footer" rendering={route} />
        </footer>
        <SitecoreKeepAlive />
      </SearchWrapper>
    </>
  );
};

export default PageLayout;
